import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../../Styles/Pricing/Pricing.css';
import '../../Styles/Partials/SalesModal.css';
import { useAuth } from '../../contexts/AuthContext';
import { Interval, pricingTiers, TRIAL_DAYS, isPaidTier } from '../../Data/PricingData';
import { useChatContext } from '../../contexts/AppDataContext';
import { CloseButton } from '../Layout/Buttons/CloseButton';
import BillingToggle from '../Pricing/BillingToggle';
import PricingCard from '../Pricing/PricingCard';
import CurrencySelect from '../Pricing/CurrencySelect';
import { useCheckout, useCurrency } from '../../hooks/useCheckout';
import { useAnalytics } from '../../hooks/useAnalytics';
import { Tier } from '../../types/user';

const SalesModal: React.FC = () => {
  const { isAuthenticated, subscription } = useAuth();
  const { showSalesModal, salesModalFeature, closeSalesModal } = useChatContext();
  const { trackPricingViewed } = useAnalytics();
  const [currency, setCurrency] = useCurrency(null, showSalesModal);
  const [billingCycle, setBillingCycle] = useState<Interval>('monthly');
  const { startCheckout, pendingTier } = useCheckout('sales_modal');
  const dialogRef = useRef<HTMLDivElement>(null);

  const currentPlan: Tier | null = isAuthenticated ? (subscription?.plan ?? 'free') : null;

  useEffect(() => {
    if (!showSalesModal) return;
    trackPricingViewed({ source: salesModalFeature ? `modal:${salesModalFeature}` : 'modal' });
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeSalesModal(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previouslyFocused?.focus?.();
    };
  }, [showSalesModal, salesModalFeature, closeSalesModal, trackPricingViewed]);

  const handleSelect = useCallback((tier: Tier) => {
    startCheckout({ tier, interval: billingCycle, currency });
  }, [startCheckout, billingCycle, currency]);

  if (!showSalesModal) return null;

  const title = salesModalFeature ? `Unlock ${salesModalFeature}` : 'Upgrade Your Plan';
  const tiers = pricingTiers.filter(t => t.idname !== 'free');

  return (
    <div className="sales-modal-overlay">
      <div
        ref={dialogRef}
        className="sales-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sales-modal-title"
        tabIndex={-1}
      >
        <div className="sales-modal-close">
          <CloseButton onClick={closeSalesModal} />
        </div>

        <header className="sales-modal-header">
          <h2 id="sales-modal-title">{title}</h2>
          <p>
            {isPaidTier(currentPlan)
              ? 'Switch plans or manage billing any time.'
              : `Both plans start with a ${TRIAL_DAYS}-day free trial. Cancel anytime.`}
          </p>
          <div className="pricing-controls">
            <BillingToggle billingCycle={billingCycle} setBillingCycle={setBillingCycle} currency={currency} />
            <CurrencySelect currency={currency} onChange={setCurrency} />
          </div>
        </header>

        <div className="sales-modal-plans">
          {tiers.map(tier => (
            <PricingCard
              key={tier.idname}
              tier={tier}
              billingCycle={billingCycle}
              currency={currency}
              currentPlan={currentPlan}
              isTrialing={subscription?.status === 'trialing'}
              pending={pendingTier === tier.idname}
              disabled={pendingTier !== null && pendingTier !== tier.idname}
              onSelect={handleSelect}
            />
          ))}
        </div>

        <p className="sales-modal-footer">
          <Link to="/pricing" onClick={closeSalesModal}>Compare All Plans</Link>
        </p>
      </div>
    </div>
  );
};

export default SalesModal;
